import { useState, useEffect } from "react";
import { projects } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, MapPin, IndianRupee, Clock, Pencil, Building2, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const statusStyles = {
  Active: "bg-[#2ECC71]/10 text-[#2ECC71] border-[#2ECC71]/20",
  Completed: "bg-[#FF5C1A]/10 text-[#FF5C1A] border-[#FF5C1A]/20",
  "On Hold": "bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/20",
};

const formatCurrency = (val) => {
  if (val >= 10000000) return `${(val / 10000000).toFixed(1)} Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(1)} L`;
  return `${(val / 1000).toFixed(0)}K`;
};

export default function Projects() {
  const [projectList, setProjectList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", site: "", value: "", progress: 0, deadline: "", status: "Active", description: "", client_name: "" });

  const fetchProjects = async () => {
    try {
      const r = await projects.list();
      setProjectList(r.data);
    } catch (err) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
    // Check for prefill from tender conversion
    const prefill = sessionStorage.getItem("project_prefill");
    if (prefill) {
      try {
        const data = JSON.parse(prefill);
        setForm({
          name: data.name || "",
          site: data.site || "",
          value: String(data.value || ""),
          progress: data.progress || 0,
          deadline: data.deadline || "",
          status: data.status || "Active",
          description: data.description || "",
          client_name: data.client_name || "",
        });
        setDialogOpen(true);
        sessionStorage.removeItem("project_prefill");
      } catch (e) { /* ignore */ }
    }
  }, []);

  const openEdit = (p) => {
    setEditId(p.id);
    setForm({ name: p.name, site: p.site, value: String(p.value), progress: p.progress, deadline: p.deadline, status: p.status, description: p.description || "", client_name: p.client_name || "" });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, value: parseFloat(form.value) || 0, progress: parseInt(form.progress) || 0 };
    try {
      if (editId) {
        await projects.update(editId, payload);
        toast.success("Project updated");
      } else {
        await projects.create(payload);
        toast.success("Project added");
      }
      setDialogOpen(false);
      setEditId(null);
      setForm({ name: "", site: "", value: "", progress: 0, deadline: "", status: "Active", description: "", client_name: "" });
      fetchProjects();
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Upgrade to Professional — WhatsApp us at +91 98765 43210", { duration: 6000 });
      } else {
        toast.error("Failed to save project");
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      await projects.remove(id);
      toast.success("Project deleted");
      fetchProjects();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const inputCls = "w-full bg-[#0A0A08] border border-[#27272A] text-[#F5F2E8] px-3 py-2.5 text-sm focus:border-[#FF5C1A] focus:ring-1 focus:ring-[#FF5C1A] outline-none transition-colors placeholder:text-[#444]";

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-3xl tracking-[2px]">PROJECTS</h2>
          <p className="text-sm text-[#888880] mt-1">Track all your active and completed projects</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditId(null); }}>
          <DialogTrigger asChild>
            <button data-testid="add-project-btn" className="bg-[#FF5C1A] text-[#0A0A08] px-5 py-2.5 font-mono text-xs font-bold tracking-widest uppercase hover:bg-[#FFB800] transition-colors flex items-center gap-2 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.5)] active:translate-y-[2px] active:shadow-none">
              <Plus size={16} /> Add Project
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#111110] border-[#27272A] text-[#F5F2E8] max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl tracking-[2px]">{editId ? "EDIT PROJECT" : "ADD PROJECT"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Project Name</label>
                <input data-testid="project-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className={inputCls} placeholder="NH-2 Road Widening Phase 1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Site Location</label>
                  <input data-testid="project-site-input" value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value })} className={inputCls} placeholder="Lucknow-Kanpur Highway" />
                </div>
                <div>
                  <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Value (INR)</label>
                  <input data-testid="project-value-input" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className={inputCls} placeholder="8400000" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Client</label>
                  <input data-testid="project-client-input" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className={inputCls} placeholder="NHAI / CPWD" />
                </div>
                <div>
                  <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Deadline</label>
                  <input data-testid="project-deadline-input" type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Progress %</label>
                  <input data-testid="project-progress-input" type="number" min="0" max="100" value={form.progress} onChange={(e) => setForm({ ...form, progress: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Status</label>
                  <select data-testid="project-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                    <option value="Active">Active</option>
                    <option value="Completed">Completed</option>
                    <option value="On Hold">On Hold</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Description</label>
                <textarea data-testid="project-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} h-20 resize-none`} />
              </div>
              <button data-testid="submit-project-btn" type="submit" className="w-full bg-[#FF5C1A] text-[#0A0A08] py-3 font-mono text-xs font-bold tracking-widest uppercase hover:bg-[#FFB800] transition-colors">
                {editId ? "Update Project" : "Add Project"}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#888880] font-mono text-sm">Loading...</div>
      ) : projectList.length === 0 ? (
        <div className="text-center py-16 bg-[#111110] border border-[#27272A]">
          <div className="text-[#888880] font-mono text-sm">No projects yet. Add your first project or convert a won tender.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {projectList.map((p) => (
            <div key={p.id} data-testid={`project-card-${p.id}`} className="bg-[#111110] border border-[#27272A] p-6 relative group hover:border-[#FF5C1A]/50 transition-colors">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold truncate">{p.name}</h3>
                  <div className="flex items-center gap-3 mt-1 text-xs text-[#888880]">
                    <span className="flex items-center gap-1"><MapPin size={12} /> {p.site || "No site"}</span>
                    {p.client_name && <span className="flex items-center gap-1"><Building2 size={12} /> {p.client_name}</span>}
                  </div>
                </div>
                <span className={`font-mono text-[10px] px-2 py-0.5 border ${statusStyles[p.status] || statusStyles.Active}`}>
                  {p.status?.toUpperCase()}
                </span>
              </div>

              <div className="mb-4">
                <div className="flex justify-between mb-1.5">
                  <span className="font-mono text-[10px] text-[#888880] tracking-widest uppercase">Progress</span>
                  <span className="font-mono text-sm text-[#FF5C1A]">{p.progress}%</span>
                </div>
                <div className="w-full h-2 bg-[#0A0A08] rounded-sm overflow-hidden">
                  <div className="h-full bg-[#FF5C1A] transition-all" style={{ width: `${p.progress}%` }} />
                </div>
              </div>

              <div className="flex items-center justify-between text-xs text-[#888880]">
                <div className="flex items-center gap-1"><IndianRupee size={12} /> <span className="font-mono text-[#FFB800]">{formatCurrency(p.value)}</span></div>
                {p.deadline && <div className="flex items-center gap-1"><Clock size={12} /> <span className="font-mono">{p.deadline}</span></div>}
              </div>

              <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                <button data-testid={`edit-project-${p.id}`} onClick={() => openEdit(p)} className="p-1.5 text-[#888880] hover:text-[#FF5C1A] transition-colors"><Pencil size={14} /></button>
                <button data-testid={`delete-project-${p.id}`} onClick={() => handleDelete(p.id)} className="p-1.5 text-[#888880] hover:text-[#E74C3C] transition-colors"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
